import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";

import Layout from "../../components/Layout";
import EditCabinetModal from "./EditCabinetModal";
import { refreshCurrentAdminUser } from "../../actions/auth.actions";

const getRoleLabel = (role) => {
    if (role === "superAdmin") return "Супер-администратор";
    if (role === "admin") return "Администратор";
    if (role === "partner") return "Партнёр";
    if (role === "storeOwner") return "Владелец офиса";
    if (role === "officeWorker") return "Сотрудник офиса";
    if (role === "cityAdmin") return "Городской администратор";
    return "Пользователь";
};

const getGenderLabel = (gender) => {
    if (gender === "male") return "Мужской";
    if (gender === "female") return "Женский";
    return gender || "—";
};

const getValue = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    return value;
};

const Cabinet = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth?.user || {});

    const [modalType, setModalType] = useState("");
    const [refreshing, setRefreshing] = useState(false);
    const [notice, setNotice] = useState(null);

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            setRefreshing(true);
            const result = await dispatch(refreshCurrentAdminUser());

            if (!mounted) return;

            if (!result?.ok) {
                setNotice({
                    type: "error",
                    message: result?.message || "Не удалось обновить профиль",
                });
            }

            setRefreshing(false);
        };

        load();

        return () => {
            mounted = false;
        };
    }, [dispatch]);

    const initials = useMemo(() => {
        const first = String(user.firstName || "").trim().charAt(0);
        const last = String(user.lastName || "").trim().charAt(0);
        return `${first}${last}`.toUpperCase() || "A";
    }, [user.firstName, user.lastName]);

    const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || "Администратор";
    const emailVerified = !!(user.emailVerified || user.isActivated || user.emailVerifiedAt);

    const personalRows = [
        ["Имя", getValue(user.firstName)],
        ["Фамилия", getValue(user.lastName)],
        ["Дата рождения", getValue(user.birthData)],
        ["Пол", getGenderLabel(user.gender)],
        ["Регион", getValue(user.regionName || user.region?.name || user.region)],
    ];

    const contactRows = [
        ["Email", getValue(user.email)],
        ["Основной телефон", getValue(user.contactNumber)],
        ["Резервный телефон", getValue(user.backupNumber)],
    ];

    const systemRows = [
        ["Роль", getRoleLabel(user.role)],
        ["Тема", user.siteThemeType === "black" ? "Тёмная" : "Светлая"],
        ["Фото", user.trafficSavings === false ? "Высокое качество" : "Экономия трафика"],
        ["ID", getValue(user._id)],
    ];

    const openModal = (type) => {
        setNotice(null);
        setModalType(type);
    };

    const handleSuccess = async (result) => {
        await dispatch(refreshCurrentAdminUser());
        setModalType("");
        setNotice({
            type: "success",
            message: result?.message || "Изменения сохранены",
        });
    };

    const renderRows = (rows) => (
        <div className="cabinetProfile__rows">
            {rows.map(([label, value]) => (
                <div className="cabinetProfile__row" key={label}>
                    <span>{label}</span>
                    <b>{value}</b>
                </div>
            ))}
        </div>
    );

    return (
        <Layout sidebar>
            <div className="cabinetProfile">
                <section className="cabinetProfile__hero">
                    <div className="cabinetProfile__identity">
                        <div className="cabinetProfile__avatar">{initials}</div>

                        <div className="cabinetProfile__identityText">
                            <span className="cabinetProfile__eyebrow">Профиль ADMIN</span>
                            <h1>{fullName}</h1>
                            <p>{user.email || "Email не указан"}</p>

                            <div className="cabinetProfile__chips">
                                <span>{getRoleLabel(user.role)}</span>
                                <span className={emailVerified ? "is-success" : "is-warning"}>
                                    {emailVerified ? "Email подтверждён" : "Email не подтверждён"}
                                </span>
                                {refreshing ? <span>Обновление...</span> : null}
                            </div>
                        </div>
                    </div>

                    <div className="cabinetProfile__heroActions">
                        <button type="button" onClick={() => openModal("personal")}>
                            <EditOutlinedIcon sx={{ fontSize: 18 }} />
                            <span>Редактировать профиль</span>
                        </button>

                        <button type="button" className="is-secondary" onClick={() => openModal("password")}>
                            <LockOutlinedIcon sx={{ fontSize: 18 }} />
                            <span>Сменить пароль</span>
                        </button>
                    </div>
                </section>

                {notice ? (
                    <div className={`cabinetProfile__notice is-${notice.type || "info"}`}>
                        {notice.type === "error" ? (
                            <ErrorOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                        ) : (
                            <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                        )}
                        <span>{notice.message}</span>
                        <button type="button" onClick={() => setNotice(null)}>×</button>
                    </div>
                ) : null}

                {user.role === "superAdmin" ? (
                    <section className="cabinetProfile__superAdmin">
                        <div>
                            <b>Управление профилями</b>
                            <p>Создание новых пользователей доступно только superAdmin. Роль superAdmin через эту форму создать нельзя.</p>
                        </div>

                        <button type="button" onClick={() => navigate("/users")}>
                            <GroupAddOutlinedIcon sx={{ fontSize: 19 }} />
                            <span>Открыть пользователей</span>
                        </button>
                    </section>
                ) : null}

                <div className="cabinetProfile__grid">
                    <section className="cabinetProfile__card">
                        <div className="cabinetProfile__cardHead">
                            <div>
                                <PersonOutlineOutlinedIcon sx={{ fontSize: 21 }} />
                                <h2>Личные данные</h2>
                            </div>
                            <button type="button" onClick={() => openModal("personal")}>Изменить</button>
                        </div>
                        {renderRows(personalRows)}
                    </section>

                    <section className="cabinetProfile__card">
                        <div className="cabinetProfile__cardHead">
                            <div>
                                <PhoneIphoneOutlinedIcon sx={{ fontSize: 21 }} />
                                <h2>Контакты</h2>
                            </div>
                            <button type="button" onClick={() => openModal("contact")}>Изменить</button>
                        </div>
                        {renderRows(contactRows)}
                    </section>

                    <section className="cabinetProfile__card">
                        <div className="cabinetProfile__cardHead">
                            <div>
                                <AlternateEmailOutlinedIcon sx={{ fontSize: 21 }} />
                                <h2>Email / логин</h2>
                            </div>
                            <button type="button" onClick={() => openModal("email")}>Изменить</button>
                        </div>

                        <div className="cabinetProfile__securityInfo">
                            <b>{user.email || "Email не указан"}</b>
                            <span className={emailVerified ? "is-success" : "is-warning"}>
                                {emailVerified ? "Подтверждён" : "Требует подтверждения"}
                            </span>

                            {!emailVerified ? (
                                <button type="button" className="cabinetProfile__verifyButton" onClick={() => openModal("verifyEmail")}>
                                    Подтвердить текущий email
                                </button>
                            ) : null}
                        </div>
                    </section>

                    <section className="cabinetProfile__card">
                        <div className="cabinetProfile__cardHead">
                            <div>
                                <SecurityOutlinedIcon sx={{ fontSize: 21 }} />
                                <h2>Безопасность</h2>
                            </div>
                            <button type="button" onClick={() => openModal("password")}>Сменить пароль</button>
                        </div>
                        {renderRows(systemRows)}
                    </section>
                </div>
            </div>

            <EditCabinetModal
                show={!!modalType}
                type={modalType}
                user={user}
                handleClose={() => setModalType("")}
                onSuccess={handleSuccess}
            />
        </Layout>
    );
};

export default Cabinet;
